import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from '@mui/material';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { NOTES, transposeChord, validateChordInput, KEY_SIGNATURES, ALL_KEYS, getRomanNumeral } from '../../utils/chordUtils';
import { generatePDF } from '../../utils/pdfUtils';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import { useTheme } from '@mui/material/styles';

const SongInput = ({ initialSong = null }) => {
  const [rawLyrics, setRawLyrics] = useState('');
  const [songData, setSongData] = useState({
    title: '',
    key: 'C',
    sections: [],
  });
  const [currentError, setCurrentError] = useState('');
  const [saving, setSaving] = useState(false);
  const [chordError, setChordError] = useState(null);
  const [showRoman, setShowRoman] = useState(false);

  const theme = useTheme();

  // Chord validation regex
  const chordRegex = /^[A-G](#|b)?(m|dim|aug|maj|sus[2|4])?[2-9]?$/;

  // Parse lyrics with section tags like [Verse], [Chorus], etc.
  const parseLyrics = (text) => {
    const sectionRegex = /\[(.*?)\]([\s\S]*?)(?=\[|$)/g;
    const sections = [];
    let match;

    while ((match = sectionRegex.exec(text)) !== null) {
      const [, type, content] = match;
      
      // Find existing section with same type to preserve chords
      const existingSection = songData.sections.find(s => s.type === type.trim());
      
      const newContent = content.trim().split('\n').map((line, index) => {
        // Try to find matching line in existing section to preserve chords
        const existingLine = existingSection?.content[index];
        return {
          lyrics: line.trim(),
          chords: existingLine?.chords || '', // Preserve existing chords if available
          id: existingLine?.id || Date.now() + Math.random(),
        };
      });

      sections.push({
        type: type.trim(),
        content: newContent,
        id: existingSection?.id || Date.now() + Math.random(),
      });
    }

    return sections;
  };

  // Update sections when raw lyrics change
  useEffect(() => {
    const sections = parseLyrics(rawLyrics);
    if (sections.length > 0) {
      setSongData(prev => ({
        ...prev,
        sections,
      }));
    }
  }, [rawLyrics]);

  // Add effect to handle initialSong
  useEffect(() => {
    if (initialSong) {
      setSongData(initialSong);
      // Convert sections back to raw lyrics format, including chords
      const rawLyricsText = initialSong.sections
        .map(section => {
          const sectionContent = section.content
            .map(line => line.lyrics)
            .join('\n');
          return `[${section.type}]\n${sectionContent}`;
        })
        .join('\n\n');
      setRawLyrics(rawLyricsText);

      // We don't need to modify the chords as they're already in the songData
    }
  }, [initialSong]);

  const handleTitleChange = (e) => {
    setSongData(prev => ({
      ...prev,
      title: e.target.value
    }));
  };

  const handleKeyChange = (e) => {
    const newKey = e.target.value;
    const oldKey = songData.key;
    if (newKey === oldKey) return;
    // Transpose all chords from oldKey to newKey
    setSongData(prev => ({
      ...prev,
      key: newKey,
      sections: prev.sections.map(section => ({
        ...section,
        content: section.content.map(line => ({
          ...line,
          chords: line.chords
            ? line.chords.split(/\s+/).map(chord => transposeChord(chord, oldKey, newKey)).join(' ')
            : ''
        }))
      }))
    }));
  };

  const handleChordChange = (sectionIndex, lineIndex, value) => {
    // Always update the chord input immediately
    setSongData(prev => {
      const newSections = [...prev.sections];
      newSections[sectionIndex].content[lineIndex].chords = value;
      return {
        ...prev,
        sections: newSections
      };
    });

    // Only validate when the user finishes typing a chord (space or end of input)
    if (value.endsWith(' ') || !value.includes(' ')) {
      const sectionType = songData.sections[sectionIndex].type;
      const validation = validateChordInput(value, sectionType, lineIndex);
      
      if (!validation.isValid) {
        setChordError(validation.error);
        setCurrentError(validation.error.message);
      } else {
        setChordError(null);
        setCurrentError('');
      }
    }
  };

  const saveSong = async () => {
    if (!auth.currentUser) return;
    
    setSaving(true);
    try {
      if (initialSong) {
        // Update existing song
        await updateDoc(doc(db, 'songs', initialSong.id), {
          ...songData,
          updatedAt: new Date()
        });
      } else {
        // Create new song
        await addDoc(collection(db, 'songs'), {
          ...songData,
          userId: auth.currentUser.uid,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      alert('Song saved successfully!');
      // Dispatch event to refresh library
      window.dispatchEvent(new CustomEvent('refreshSongLibrary'));
    } catch (error) {
      setCurrentError('Error saving song: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const exportToPDF = async () => {
    try {
      const pdfBytes = await generatePDF(songData);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${songData.title || 'song'}.pdf`;
      link.click();
    } catch (error) {
      setCurrentError('Error generating PDF: ' + error.message);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Add New Song
      </Typography>

      {/* Title and Key Selection */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Song Title"
            value={songData.title}
            onChange={handleTitleChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Key</InputLabel>
            <Select
              value={songData.key}
              label="Key"
              onChange={handleKeyChange}
            >
              {Object.keys(ALL_KEYS).map(note => (
                <MenuItem key={note} value={note}>{note}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Raw Lyrics Input */}
      <Paper sx={{ p: 2, my: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Paste Lyrics with Section Tags
        </Typography>
        <Typography variant="caption" display="block" sx={{ mb: 1 }}>
          Use tags like [Verse], [Chorus], [Bridge] to mark sections.
          Example:
          [Verse]
          First verse lyrics here
          Another line here
          [Chorus]
          Chorus lyrics here
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={8}
          value={rawLyrics}
          onChange={(e) => setRawLyrics(e.target.value)}
          placeholder="Paste lyrics with section tags..."
        />
      </Paper>

      {/* Chord Input for Each Section */}
      {songData.sections.map((section, sectionIndex) => (
        <Paper key={section.id} sx={{ p: 2, my: 2, bgcolor: theme.palette.background.paper }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ color: theme.palette.secondary.main, fontWeight: 700 }}>
            {section.type}
          </Typography>
          {section.content.map((line, lineIndex) => (
            <Box key={line.id} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Chords"
                value={line.chords}
                onChange={(e) => handleChordChange(sectionIndex, lineIndex, e.target.value)}
                placeholder="Use spaces or underscores for positioning (e.g., Am__G___F C)"
                sx={{
                  mb: 1,
                  backgroundColor: theme.palette.background.default,
                  input: { color: theme.palette.text.primary },
                  label: { color: theme.palette.text.secondary },
                  '& .MuiInputBase-root': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                }}
                error={chordError?.section === section.type && chordError?.lineIndex === lineIndex}
                helperText={
                  chordError?.section === section.type && 
                  chordError?.lineIndex === lineIndex ? 
                  `Invalid chord "${chordError.chord}" at position ${chordError.position + 1}` :
                  `Use spaces or underscores (_) to position chords. Display: ` +
                  (line.chords
                    ? line.chords.split(/\s+/).map(chord => {
                        const numeral = getRomanNumeral(chord, songData.key);
                        return numeral ? `${transposeChord(chord, songData.key, songData.key)} (${numeral})` : transposeChord(chord, songData.key, songData.key);
                      }).join(' ')
                    : '')
                }
              />
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre',
                  color: '#111',
                  fontWeight: 700,
                  mb: 0.2,
                  fontSize: '1.05rem',
                }}
              >
                {line.chords
                  ? line.chords.split(/\s+/).map(chord =>
                      transposeChord(chord, songData.key, songData.key)
                    ).join(' ')
                  : ''}
              </Typography>
              <Typography sx={{
                fontFamily: 'monospace',
                whiteSpace: 'pre',
                color: '#444',
                mb: 1,
                fontSize: '1.05rem',
              }}>
                {line.lyrics}
              </Typography>
            </Box>
          ))}
        </Paper>
      ))}

      {currentError && (
        <Alert severity="error" sx={{ my: 2 }}>
          {currentError}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          sx={{
            backgroundColor: theme.palette.highlight.main,
            color: theme.palette.highlight.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
            },
            fontWeight: 700,
            fontSize: '1.08rem',
            minWidth: 140,
          }}
          onClick={saveSong}
          disabled={!!currentError || !songData.title || saving}
        >
          {saving ? 'Saving...' : 'Save Song'}
        </Button>
        <Button 
          variant="contained" 
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.highlight.main,
              color: '#fff',
            },
            fontWeight: 700,
            fontSize: '1.08rem',
            minWidth: 140,
          }}
          onClick={exportToPDF}
          disabled={!songData.title}
        >
          Export PDF
        </Button>
      </Box>

      {/* Preview Section with updated styling */}
      <Paper sx={{ mt: 4, p: 3, bgcolor: theme.palette.background.paper }}>
        <Typography variant="h5" gutterBottom>
          Preview
        </Typography>
        <Typography variant="h6" gutterBottom>
          {songData.title || 'Untitled Song'} - Key: {songData.key}
        </Typography>
        <Slide in direction="down">
          <Box sx={{ mb: 2 }}>
            <Button
              variant={showRoman ? 'contained' : 'outlined'}
              color="error"
              onClick={() => setShowRoman((prev) => !prev)}
              sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1rem' }, minWidth: 180 }}
            >
              {showRoman ? 'Hide Numbers' : 'Show Numbers'}
            </Button>
          </Box>
        </Slide>
        <Fade in>
          <Box>
            {songData.sections.map((section) => (
              <Box key={section.id} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: theme.palette.secondary.main, fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  {section.type}
                </Typography>
                {section.content.map((line) => (
                  <Box key={line.id}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre',
                        color: theme.palette.highlight.main,
                        fontWeight: 700,
                        mb: 0.2,
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      }}
                    >
                      {line.chords
                        ? line.chords.split(/(\s+)/).map((part, idx) =>
                            part.trim()
                              ? transposeChord(part, songData.key, songData.key)
                              : part
                          ).join('')
                        : ''}
                    </Typography>
                    {showRoman && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          whiteSpace: 'pre',
                          color: theme.palette.primary.main,
                          fontWeight: 700,
                          mb: 0.5,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        }}
                      >
                        {line.chords
                          ? line.chords.split(/(\s+)/).map((part, idx) =>
                              part.trim()
                                ? getRomanNumeral(part, songData.key) || '-'
                                : part
                            ).join('')
                          : ''}
                      </Typography>
                    )}
                    <Typography sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre',
                      color: theme.palette.text.primary,
                      mb: 1,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                    }}>
                      {line.lyrics}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Fade>
      </Paper>
    </Box>
  );
};

export default SongInput; 